"use client";

import { Minus, Plus } from "lucide-react";
import { motion } from "motion/react";
import { usePressedInteraction } from "@/components/ui/motion-primitives";

export interface QuantityStepperProps {
  readonly disabled?: boolean;
  readonly max?: number;
  readonly min?: number;
  readonly onChange: (quantity: number) => void;
  readonly value: number;
}

export function QuantityStepper({
  disabled = false,
  max = 999,
  min = 1,
  onChange,
  value,
}: Readonly<QuantityStepperProps>) {
  const normalizedValue = Math.min(max, Math.max(min, value));
  const interaction = usePressedInteraction();

  return (
    <div className="inline-flex h-12 overflow-hidden rounded-lg border border-outline-variant bg-surface">
      <motion.button
        aria-label="Reducir cantidad"
        className="inline-flex w-12 shrink-0 items-center justify-center text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:text-outline"
        disabled={disabled || normalizedValue <= min}
        onClick={() => onChange(normalizedValue - 1)}
        type="button"
        {...interaction}
      >
        <Minus aria-hidden="true" size={16} />
      </motion.button>
      <input
        aria-label="Cantidad"
        className="min-w-0 flex-1 border-x border-outline-variant bg-surface text-center text-sm font-bold outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        disabled={disabled}
        inputMode="numeric"
        max={max}
        min={min}
        onChange={(event) => onChange(Number(event.target.value))}
        type="number"
        value={normalizedValue}
      />
      <motion.button
        aria-label="Aumentar cantidad"
        className="inline-flex w-12 shrink-0 items-center justify-center text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:text-outline"
        disabled={disabled || normalizedValue >= max}
        onClick={() => onChange(normalizedValue + 1)}
        type="button"
        {...interaction}
      >
        <Plus aria-hidden="true" size={16} />
      </motion.button>
    </div>
  );
}
